import { imgixHost } from 'src/config'
import Model, { encoders, ID, Integer, JsonObject, MODEL_LEVEL } from 'src/models/model'
import Archivable from 'src/models/model/concerns/Archivable'
import Librariable from 'src/models/model/concerns/Librariable'
import ReportCategory from 'src/models/report/ReportCategory'
import ReportElement from 'src/models/report/ReportElement'
import ReportStatus, { DATA_PRESENCE } from 'src/models/report/ReportStatus'
import User from 'src/models/user/User'
import { getOptionsWithSeparators } from 'src/modules/admin/report/ReportUtils'
import { nextTick } from 'vue'

export const optionNamesForControlName = (optionName) => {
  if (optionName === 'date_range') {
    return ['start_date', 'end_date']
  }
  if (optionName === 'select_multiple_membership_types') {
    return ['include_membership_type_ids']
  }
  if (optionName === 'select_multiple_movies') {
    return ['include_movie_ids', 'exclude_movie_ids']
  }
  if (optionName === 'select_multiple_item_classes') {
    return ['include_item_class_ids', 'exclude_item_class_ids']
  }
  if (optionName === 'select_multiple_title_classes') {
    return ['include_title_class_ids', 'exclude_title_class_ids']
  }

  return [optionName]
}

const NEVER_SAVE_REPORT_OPTIONS = ['start_date', 'end_date', 'date']

export const isOptionSet = (reportOptions, control) => {
  return _.some(optionNamesForControlName(control), name =>
    reportOptions[name] !== null && reportOptions[name] !== undefined
  )
}

export const unsetOption = (reportOptions, control) => {
  _.each(optionNamesForControlName(control), name => {
    reportOptions[name] = null
  })
}

export const setDefaultOption = (reportOptions, finalOptions, control) => {
  _.each(optionNamesForControlName(control), name => {
    reportOptions[name] = finalOptions[name]
  })
}

export const SYSTEM_REPORTS = {
  TRIAL_BALANCE: 'trial_balance',
  DATA_TABLE_SOURCE_REPORT: 'data_table_source_report',
  OPTIONS_LOADER_REPORT: 'options_loader_report',
  DATA_TABLE_COMPARISON_REPORT: 'data_table_comparison_report',
  DATA_TABLE_TREND_REPORT: 'data_table_trend_report',
  DATA_TABLE_REPORT: 'data_table_report',
  CONFIGURATION_AUDIT_DETAIL_REPORT: 'configuration_audit_detail',
  REGISTER_CLIENT_MEMORY_REPORT: 'register_client_memory_report',
  MALFORMED_ORDERS: 'malformed_orders',
  BOX_OFFICE_SETTLEMENT_REPORT: 'box_office_settlement_report'
}

export const REPORT_ACTIONS = {
  RUN_REPORT: 'run_report',
  CHECK_STATUS: 'check_status',
  REVALIDATE: 'revalidate',
  FORCE_RECOMPUTE: 'force_recompute',
  FORCE_RECOMPUTE_DATA: 'force_recompute_data',
  MIGRATE_REPORT: 'migrate_report',
  ARCHIVE_REPORT: 'archive',
  MAKE_ATLAS_REPORT: 'make_atlas_report',
  DOWNLOAD_PDF: 'download_pdf',
  EMAIL: 'email'
}

export const REPORT_CONTROL_CATEGORIES = {
  'Data and Filtering': [
    'data_source_name',
    'series_name',
    'group_by',
    'sort_direction',
    'sort_by',
    'output_type',
    'output_types',
    'x_axis_output_type',
    'y_axis_output_type',
    'size_of_bubble',
    'column_order',
    'descending',
    'report_level',
    'region_report_level',

    'void_mode',
    'configuration_type',
    'configuration_audit_type',
    'configuration_audit_level',
    'movie_id',
    'studio',
    'item_class',
    'inventory_item',
    'inventory_recipe',
    'title_class',
    'feature_name',

    'ticket_value_mode',
    'show_full_date_in_showing',

    'select_multiple_membership_types',
    'select_multiple_movies',
    'select_multiple_movies',
    'select_multiple_item_classes',
    'select_multiple_item_classes',
    'select_multiple_title_classes',
    'select_multiple_title_classes',
    'select_multiple_register_stations',
    'select_multiple_register_stations',
    'select_multiple_showing_badges',
    'select_multiple_showing_badges',

    'employee_id',
    'location_id',
    'register_station',
    'group_by_showing_badge_ids',
    'report_filters',
    'per_item',
    'per_ticket',
    'per_order',

    'group_by_tax',
    'group_by_class',
    'group_by_date',
    'group_by_screen',
    'group_by_movie',
    'group_by_studio',
    'group_by_showing',
    'group_by_ticket_type_name',
    'group_by_ticket_type_reporting_name',
    'group_by_ticket_type_display_name',
    'group_by_item_class',
    'group_by_item',
    'group_by_item_variation',
    'group_by_price_card'
  ],
  Display: [
    'has_custom_title',
    'custom_title',
    'custom_description',
    'display_icon',

    'chart_type',
    'chart_display_type',
    'include_charts',

    'show_legend',
    'show_bubble_label',
    'percent_stacking',
    'component_width',
    'height',
    'top_n',

    'exclude_zeros',
    'net_gross',
    'void_type',

    'compact_table',
    'plain_table_content',
    'count_only',
    'subtotals',
    'only_show_number',
    'hide_prior_indicators',
    'number_mode',
    'line_chart_mode',
    'sort_groups',
    'group_share_columns'
  ],
  Schedule: [
    'show_time_header',
    'show_time_slot_header',
    'show_time_screen_labels',
    'show_times',
    'show_time_chips',
    'show_seat_chart',
    'show_rating',
    'show_price_card',
    'show_showing_badges',
    'show_posters',
    'show_progress_bar',
    'highlight_starting_showings',
    'highlight_ending_showings',
    'highlight_current_showings',
    'hide_distance_showings',
    'color_by_occupancy',
    'show_relative_times',
    'show_relative_time_chips',
    'show_current_time',
    'distant_showing_threshold_hours',
    'zoom'
  ],
  Flyer: [
    'disclaimer',
    'footer_image',
    'price_card_notes',
    'hide_price_card',
    'description_limit',
    'rating_type'
  ],
  Other: [
    'max_wait_time',
    'max_employees',

    'published_mode',
    'start_end',
    'public_only',
    'report_by_email',
    'has_invoice_date',
    'credit_debit',
    'basis',
    'tax_mode'
  ],
  Date: [
    'date',
    'date_range',
    'date_range',
    'relative_date',
    'relative_start_date',
    'relative_end_date',
    'interval',
    'ticket_time_mode',
    'effective_type',
    'dispute_time_mode'
  ]
}

// Given a list of controls, return them categorized and sorted according to defined order in REPORT_CONTROL_CATEGORIES
export const categorizeControls = function (controls) {
  controls = [...controls]

  const categorizedControls = {}
  _.each(REPORT_CONTROL_CATEGORIES, (categoryControls, category) => {
    if (!_.some(controls, control => _.includes(categoryControls, control))) return

    categorizedControls[category] ||= []

    _(categoryControls).each(categoryControl => {
      if (_.includes(controls, categoryControl)) {
        categorizedControls[category].push(categoryControl)
        controls = _.without(controls, categoryControl)
      }
    })
  })

  if (controls.length) {
    controls.Other ||= []
    controls.Other.push(...controls)
  }

  return categorizedControls
}

export default class Report extends Model {
  static name = 'Report'

  static versioned = true

  static level = MODEL_LEVEL.CIRCUIT

  static employeeRequiredPermissionCategories = [Model.permissionCategories.OperationsReporting]

  static atlasable = true

  static define () {
    this.concerns = [Archivable, Librariable]

    this.fields = {
      id: {
        type: ID
      },
      circuitId: {
        type: ID,
        output: true,
        excludeFromDefault: true
      },
      name: {
        type: String
      },
      displayOrder: {
        type: Integer,
        default: 9999999
      },
      icon: {
        type: String,
        default: 'insert_chart_outlined'
      },
      description: {
        type: String,
        default: ''
      },
      title: {
        type: String,
        output: true
      },
      hasCustomTitle: {
        type: Boolean
      },
      customTitle: {
        type: String
      },
      subtitle: {
        type: String,
        output: true
      },
      hasCustomSubtitle: {
        type: Boolean
      },
      customSubtitle: {
        type: String
      },
      reportCategory: {
        type: ReportCategory,
        encode: encoders.hasOne,
        reference: true,
        default: null
      },
      // Used when saving a report as a copy to look up the category to link to
      categoryName: {
        type: String,
        default: null
      },
      deprecatedComponentCount: {
        type: Integer,
        excludeFromDefault: true,
        output: true
      },
      scheduledReportCount: {
        type: Integer,
        excludeFromDefault: true,
        output: true
      },
      scheduledReportDescriptions: {
        type: JsonObject,
        excludeFromDefault: true,
        output: true
      },

      pdfLandscape: {
        type: Boolean,
        default: true
      },
      pdfWidth: {
        type: Integer,
        default: 1800
      },
      pdfMargins: {
        type: Boolean,
        default: false
      },
      pdfHeader: {
        type: Boolean,
        default: true
      },

      mobileEmailWidth: {
        type: Integer,
        default: 500
      },
      mobileEmailScale: {
        type: Integer,
        default: 200
      },

      desktopEmailWidth: {
        type: Integer,
        default: 1200
      },
      desktopEmailScale: {
        type: Integer,
        default: 200
      },

      maxEmailImageHeight: {
        type: Integer,
        default: 15000
      },

      includeCsv: {
        type: Boolean,
        default: true,
        input: true
      },

      defaultOptions: {
        type: JsonObject
      },
      allControls: {
        type: [String],
        output: true
      },

      user: {
        type: User,
        reference: true,
        encode: encoders.hasOne
      },

      // Report Running
      controls: {
        type: [String]
      },
      initialOptions: {
        type: JsonObject,
        output: true
      },
      possibleOptionsValues: {
        type: JsonObject,
        output: true,
        excludeFromDefault: true
      },
      reportOptions: {
        type: JsonObject,
        default () {
          return {}
        },
        input: true
      },

      elementReportOptions: {
        type: JsonObject,
        default () {
          return {}
        },
        input: true
      },

      timeZone: {
        type: String,
        output: true
      },

      // Report output
      elements: {
        type: [ReportElement],
        output: true
      },
      logoImage: {
        type: String,
        output: true
      },
      pdfData: {
        type: String,
        output: true
      },
      combinedCsv: {
        type: String,
        output: true
      },
      combinedCsvWithTotals: {
        type: String,
        output: true
      },
      currency: {
        type: String,
        output: true
      },
      publicReportUrl: {
        type: String,
        output: true
      },
      publicReportExportUrl: {
        type: String,
        output: true
      },
      reportStatus: {
        type: ReportStatus,
        output: true
      },

      action: {
        type: String,
        input: true,
        default: null
      },
      token: {
        type: String,
        input: true,
        default: null
      },
      email: {
        type: String,
        input: true
      },
      regionReport: {
        type: Boolean,
        default: false
      },
      atlasParentId: {
        type: ID,
        excludeFromDefault: true
      },
      atlas: {
        type: Boolean,
        default: false
      },

      // Initial loading is used to indicate we have changed the requested
      // report information and what is displayed does not match the options
      // We show a loading indicator while we fetch the initial status of
      // the report. Once it is loaded, we stop showing a loading indicator
      // on fetch because we have status embedded in the report components
      // and in the report headers
      initialLoading: {
        type: Boolean,
        local: true
      },
      reportViews: {
        type: Integer,
        excludeFromDefault: true,
        output: true
      },
      reportFiltersModified: {
        type: Boolean,
        local: true,
        default: false
      }
    }

    this.computed = {
      logoImageUrl () {
        if (!this.logoImage) return ''

        return `${imgixHost}/${this.logoImage}?${this.logoImageParams}`
      },
      fullTitle () {
        return this.title + (this.subtitle ? (' - ' + this.subtitle) : '')
      },
      essential () {
        return this.atlas
      },
      columns () {
        return _(this.elements.data).map('columns').flatten().compact().uniqBy('field').value()
      },
      elementChangeCount () {
        const elementChanges = _.filter(this.elements.data, element => {
          return !_.isEqual(this.elementReportOptions[element.id], element.defaultOptions)
        }).length

        return elementChanges + (this.reportFiltersModified ? 1 : 0)
      }
    }

    this.watchers = [
      // When report controls are removed, remove their corresponding option
      // values so they are ignored in subsequent report runs
      [
        function () { return this.controls },
        function () {
          const removedControls = _.difference(this.previousControls, this.controls)
          _.each(removedControls, (control) => unsetOption(this.reportOptions, control))
          this.previousControls = this.controls
        }
      ],
      // When report options change, unsubscribe from the report status checks
      [
        function () { return [this.reportOptions, this.elementReportOptions] },
        function () {
          if (!_.isEqual(this.reportOptions, this.priorReportOptions) || !_.isEqual(this.elementReportOptions, this.priorElementReportOptions)) {
            this.reportStatusRecheckTimer && clearTimeout(this.reportStatusRecheckTimer)

            this.priorReportOptions = _.cloneDeep(this.reportOptions)
            this.priorElementReportOptions = _.cloneDeep(this.elementReportOptions)
          }
        }
      ]
    ]

    this.paginate = true
  }

  get filterableColumns () {
    return _.filter(this.columns, 'filterable')
  }

  get hasFilters () {
    return _.some(this.filterableColumns, column => {
      return this.isFieldFiltered(column.field)
    })
  }

  get filteredColumns () {
    return _.filter(this.filterableColumns, column => {
      return this.isFieldFiltered(column.field)
    })
  }

  get filterDescription () {
    return _.map(this.filteredColumns, column => {
      const field = column.field
      let values = this.reportOptions[`${field}_filter`]
      if (_.isString(values)) {
        values = values.split(',')
      }
      const exclude = this.reportOptions[`exclude_${field}`]

      if (!values.length) return ''

      const valuesString =
        values.length === 1
          ? values[0]
          : `${values.slice(0, -1).join(', ')}${values.length > 2 ? ',' : ''} or ${values.slice(-1)}`

      return `${column.label} is${exclude ? ' not ' : ''} ${valuesString}`
    }).join(', ')
  }

  groupbyOptionsWithSeparators (groupBySearchValue) {
    const options = _.map(this.possibleOptionsValues.group_by, value => {
      const isNamedValue = value.name || value.label
      const columnOption = _.find(this.columns, {
        field: isNamedValue ? (value.value || value.id) : value
      })
      const optionValue = isNamedValue ? (value.value || value.id) : value
      return {
        value: optionValue,
        label: isNamedValue ? (value.label || value.name) : _.startCase(value),
        category: columnOption?.category || 'Other',
        definition: columnOption?.definition,
        key: `option-${optionValue}`
      }
    })

    return getOptionsWithSeparators(options, [], groupBySearchValue)
  }

  filterableColumnsOptionsWithSeparators (filterableColumnsSearchValue) {
    const options = _.map(this.filterableColumns, column => {
      return {
        value: column.field,
        label: column.label || _.startCase(column.field),
        category: column.category || 'Other',
        definition: column.definition,
        key: `option-${column.field}`
      }
    })

    return getOptionsWithSeparators(options, [], filterableColumnsSearchValue)
  }

  isFieldFiltered (field) {
    return !!(this.reportOptions && this.reportOptions[`${field}_filter`] && this.reportOptions[`${field}_filter`].length)
  }

  isFieldExcludeFiltered (field) {
    return this.isFieldFiltered && this.reportOptions[`exclude_${field}`]
  }

  resetFieldFilter (field) {
    this.reportOptions[`${field}_filter`] = []
  }

  resetAllFilters (reportOptions = null) {
    reportOptions ||= this.reportOptions
    _.each(this.filterableColumns, column => {
      const field = column.field
      reportOptions[`exclude_${field}`] = false
      reportOptions[`${field}_filter`] = []
    })

    this.cleanupFilterOptions()
  }

  /**
   * Running a report requests it to be run and starts then regularly checks the status of the
   * report until it is done running.
   * action = 'run_report' | 'revalidate' | 'force_recompute' | 'force_recompute_data'
   */
  async runReport (action = REPORT_ACTIONS.RUN_REPORT) {
    // Set values from each report element
    _.each(this.elements.data, element => {
      this.elementReportOptions[element.id] = element.reportOptions
    })

    // Reset the status check count and start checking the status
    this.statusCheckCount = null
    this.checkReportStatus(action)
  }

  /**
   * This method is used to consolidate many simultaneously `runReport()` calls into one.
   * Some report components can trigger a status check while rendering, and a report with many
   * such components can trigger many `runReport()` calls at once upon render.
   */
  debouncedRunReport () {
    if (!this._debouncedRunReport) {
      // We just need enough time for some Vue render cycles to complete
      const debounceWaitMs = 50
      this._debouncedRunReport = _.debounce(this.runReport, debounceWaitMs)
    }
    this._debouncedRunReport()
  }

  async checkReportStatus (action = REPORT_ACTIONS.CHECK_STATUS) {
    // If the report is no longer used, do not check the status
    if (this.destroyed) return

    // Handle initializing the status checking and displaying what we are doing
    const optionsHaveChanged =
      !_.isEqual(this.reportOptions, this.priorReportOptions) ||
      !_.isEqual(this.elementReportOptions, this.priorElementReportOptions)

    const currentlyCheckingStatus = !!this.statusCheckCount

    if (!currentlyCheckingStatus || optionsHaveChanged) {
      // Initialize status checking
      this.initialLoading = true
      this.statusCheckCount = null
      this.reportStatusRecheckTimer && clearTimeout(this.reportStatusRecheckTimer)
    } else {
      // Mark the initial loading as complete
      this.initialLoading = false
    }

    if (optionsHaveChanged) {
      this.priorReportOptions = _.cloneDeep(this.reportOptions)
      this.priorElementReportOptions = _.cloneDeep(this.elementReportOptions)
    }

    const statusReport = new Report({
      id: this.id,
      defaultOptions: this.defaultOptions,
      reportOptions: this.reportOptions,
      elementReportOptions: this.elementReportOptions,
      action,
      token: this.token,
      atlas: this.atlas
    }, {
      fields: ['id'],
      reportStatus: true,
      elements: {
        fields: ['id'],
        reportStatus: true
      },
      skipWatchers: true
    })
    let response
    try {
      // If this is a new request, this will start building the report
      // if this is an existing request, we will get the ongoing status
      response = await statusReport.save()
    } catch (error) {
      this.$rollbar.error('Error running report', error)
    }

    if (response && response.errors && response.errors.length) {
      this.initialLoading = false
      console.log('Error running report')
      console.log(response)
      return
    }

    if (statusReport.reportStatus.done || statusReport.reportStatus.notBuilding) {
      // Report is done, we can stop showing loading, and stop fetching the status
      this.statusCheckCount = null
      this.initialLoading = false
      this.elementsDoneCount = null

      if (statusReport.reportStatus.done || statusReport.reportStatus.error) {
        this.loadReportData()
      }
    } else {
      // The report is not done running, we need to set up a timer to refetch status

      // Set the report status from the status report check, but if we have data locally
      // and the new status has no data, use the existing data status
      let existingDataPresence = DATA_PRESENCE.NONE
      let hasExistingData = false
      if (this.reportStatus) {
        existingDataPresence = this.reportStatus.dataPresence
        hasExistingData = !this.reportStatus.noData
      }

      this.reportStatus = statusReport.reportStatus
      if (this.reportStatus.noData && hasExistingData) {
        this.reportStatus.dataPresence = existingDataPresence
      }
      let elementsDoneCount = 0
      _.each(this.elements.data, element => {
        const existingDataPresence = element.reportStatus.dataPresence
        const hasExistingData = !element.reportStatus.noData
        const reportStatusElement = _.find(statusReport.elements.data, { id: element.id })
        if (reportStatusElement) {
          element.reportStatus = reportStatusElement.reportStatus
        }
        if (element.reportStatus.noData && hasExistingData) {
          element.reportStatus.dataPresence = existingDataPresence
        }
        if (element.reportStatus.done) {
          elementsDoneCount++
        }
      })

      // Make sure we aren't already doing a status check on this report object
      // (a time this can happend is when retriggered a report while it was running)
      this.reportStatusRecheckTimer && clearTimeout(this.reportStatusRecheckTimer)

      this.statusCheckCount = (this.statusCheckCount || 0) + 1

      let statusCheckDelayMs
      if (this.statusCheckCount === 1) {
        statusCheckDelayMs = 0
      } else if (this.statusCheckCount <= 50) {
        // We want to check more often within the first 10 seconds just in case
        // an external service is waiting for the results (e.g. PDFCrowd)
        statusCheckDelayMs = 500
      } else if (this.statusCheckCount <= 100) {
        statusCheckDelayMs = 1000
      } else if (this.statusCheckCount <= 200) {
        statusCheckDelayMs = 2000
      } else {
        statusCheckDelayMs = 4000
      }

      // See if we have new data for any of the elements and if so, load the report data
      if (this.statusCheckCount > 0) {
        if (elementsDoneCount !== this.elementsDoneCount) {
          this.loadCompletedReportElementData()
        }
        this.elementsDoneCount = elementsDoneCount
      }

      // Set up the time to recheck the status of the report
      this.reportStatusRecheckTimer = setTimeout(async () => {
        // "Run" the report again will return the current data and
        // the current status of the report
        await this.checkReportStatus()
      }, statusCheckDelayMs)
    }
  }

  async loadCompletedReportElementData () {
    const report = new Report({
      id: this.id,
      defaultOptions: this.defaultOptions,
      reportOptions: this.reportOptions,
      displayOrder: this.displayOrder,
      elementReportOptions: this.elementReportOptions,
      action: REPORT_ACTIONS.CHECK_STATUS,
      token: this.token,
      atlas: this.atlas
    }, {
      watchQuery: false,
      elements: {
        reportStatus: 'default',

        controls: 'default',
        possibleOptionsValues: 'default',

        chart: {
          columns: 'default'
        },
        table: {
          columns: 'default'
        },
        view: 'default',
        widget: {
          columns: 'default',
          sparkline: 'default'
        },
        html: 'default',
        rawData: 'default'
      }
    })

    try {
      // If this is a new request, this will start building the report
      // if this is an existing request, we will get the ongoing status
      await report.save()
    } catch (error) {
      this.$rollbar.error('Error running report', error)
    } finally {
      this.resetField('action')
    }

    const newElements = [...this.elements.data]
    _.each(report.elements.data, (element, index) => {
      if (element.reportStatus.done) {
        newElements[index] = element
      }
    })
    this.elements.data = newElements
    report.destroy()
  }

  async loadReportData () {
    this.action = REPORT_ACTIONS.CHECK_STATUS

    let response
    try {
      // If this is a new request, this will start building the report
      // if this is an existing request, we will get the ongoing status
      response = await this.save()
      this.initialLoading = false
    } catch (error) {
      this.$rollbar.error('Error running report', error)
    } finally {
      this.resetField('action')
    }

    if (response && response.errors && response.errors.length) {
      console.log('Error running report')
      console.log(response)
    }
  }

  async migrateReport () {
    this.action = REPORT_ACTIONS.MIGRATE_REPORT

    let response
    try {
      response = await this.save()
    } catch (error) {
      this.$rollbar.error('Failed to migrateReport(), error: ', error)
    } finally {
      this.resetField('action')
    }

    if (response && response.errors && response.errors.length) {
      console.log('Error migrating report')
      console.log(response)
    }

    return response
  }

  async makeAtlasReport () {
    this.action = REPORT_ACTIONS.MAKE_ATLAS_REPORT

    let response
    try {
      response = await this.save()
    } catch (error) {
      this.$rollbar.error('Failed to makeAtlasReport(), error: ', error)
    } finally {
      this.resetField('action')
    }

    return response
  }

  async archiveReport () {
    this.action = REPORT_ACTIONS.ARCHIVE_REPORT

    let response
    try {
      response = await this.save()
    } catch (error) {
      this.$rollbar.error('Failed to archiveReport(), error: ', error)
    } finally {
      this.resetField('action')
    }

    return response
  }

  async forceRecompute () {
    await this.runReport(REPORT_ACTIONS.FORCE_RECOMPUTE)
  }

  async forceRecomputeData () {
    await this.runReport(REPORT_ACTIONS.FORCE_RECOMPUTE_DATA)
  }

  async revalidate () {
    await this.runReport(REPORT_ACTIONS.REVALIDATE)
  }

  async downloadPdf () {
    this.action = REPORT_ACTIONS.DOWNLOAD_PDF

    let errors
    try {
      const result = await this.save()
      errors = result.errors
    } catch (error) {
      this.$rollbar.error('Error downloading PDF', error)
    } finally {
      this.resetField('action')
    }

    return errors
  }

  async sendEmail () {
    this.action = REPORT_ACTIONS.EMAIL

    let errors
    try {
      const result = await this.save()
      errors = result.errors
    } catch (error) {
      this.$rollbar.error('Error sending email', error)
    } finally {
      this.resetField('action')
    }

    return errors
  }

  afterInflate () {
    this.initializeOptions()
  }

  cleanupFilterOptions () {
    const keysToCleanup = []
    _.each(_.keys(this.initialOptions || {}), key => {
      if ((key.endsWith('_filter') || key.startsWith('exclude_')) &&
          (this.reportOptions[key] === null)) {
        keysToCleanup.push(key)
      }
    })

    _.each(keysToCleanup, key => {
      delete this.reportOptions[key]
      delete this.initialOptions[key]
      delete this.defaultOptions[key]
    })

    return keysToCleanup.length > 0
  }

  async initializeOptions () {
    this.initializing = true

    if (!this.initialOptions || !this.reportOptions) return
    this.cleanupFilterOptions()

    // Add each control name to ensure reactivity
    _.each(_.keys(this.initialOptions), key => {
      if (this.reportOptions[key] !== null && this.reportOptions[key] !== undefined) return
      this.reportOptions[key] = this.initialOptions[key]
    })

    // Need to set as new object so all values are reactive
    this.reportOptions = { ...this.reportOptions }

    // Add each control name to ensure reactivity
    // TODO: Convert to handle multiple keys
    _.each(this.allControls, control => {
      const key = optionNamesForControlName(control)
      if (this.defaultOptions[key] !== null && this.defaultOptions[key] !== undefined) return

      this.defaultOptions[key] = undefined
    })

    // Need to set as new object so all values are reactive
    this.defaultOptions = { ...this.defaultOptions }

    // Add in preview options to each element
    _.each(this.elements?.data, element => {
      this.elementReportOptions[element.id] ||= {}

      element.reportOptions = {
        ...element.defaultOptions,
        ...this.elementReportOptions[element.id]
      }
    })

    // Need to set as new object so all values are reactive
    this.elementReportOptions = { ...this.elementReportOptions }

    await nextTick()

    this.initializing = false
  }

  optionsForSelect (optionType) {
    return _.map(this.possibleOptionsValues[optionType], value => {
      if (value.name || value.label) {
        return {
          value: value.value || value.id,
          label: value.label || value.name
        }
      } else {
        return { value, label: _.startCase(value) }
      }
    })
  }

  columnNamesForSelect (columnSearchValue) {
    if (!this.possibleOptionsValues?.column_order) {
      return []
    }
    const filteredColumns = this.possibleOptionsValues.column_order.filter(column =>
      !columnSearchValue ||
      column?.label?.toLowerCase().includes(columnSearchValue.toLowerCase()) ||
      column?.category?.toLowerCase().includes(columnSearchValue.toLowerCase())
    )

    const options = filteredColumns.map(column => ({
      value: column.field,
      label: column.label || _.startCase(column.field),
      category: column.category || 'Other',
      key: `option-${column.field}`,
      definition: column.definition
    }))

    return getOptionsWithSeparators(options, [], columnSearchValue)
  }

  get scopeType () {
    if (this.user) {
      return 'Personal'
    } else if (this.essential) {
      return 'Essential'
    } else {
      return 'Circuit'
    }
  }

  beforeDestroy () {
    this.reportStatusRecheckTimer && clearTimeout(this.reportStatusRecheckTimer)
  }

  async saveAsCopy () {
    const newReport = this.copy()

    // Set the name to indicate it's a copy
    newReport.name = `Copy of ${this.name}`
    newReport.atlas = false
    newReport.user = Model.context.currentUser

    newReport.reportCategory = this.reportCategory

    // Create a deep copy of the elements data to avoid modifying the original
    const newElements = _.map(this.elements.data, element => {
      const elementCopy = element.copy()
      elementCopy.defaultOptions = { ...element.reportOptions }

      return elementCopy
    })

    // Save the report first to get a real ID
    const result = await newReport.save()
    const errors = result.errors

    if (errors && errors.length > 0) {
      throw new Error('Error duplicating report')
    }

    for (const element of newElements) {
      element.reportId = newReport.id

      await element.save()
    }

    await newReport.fetch()
    return newReport
  }

  async publishChanges () {
    // Save report-level changes
    if (!_.isEqual(this.reportOptions, this.initialOptions)) {
      const reportOptionsToSave = _.omit(this.reportOptions, NEVER_SAVE_REPORT_OPTIONS)
      this.defaultOptions = { ...this.defaultOptions, ...reportOptionsToSave }
      this.reportFiltersModified = false
      await this.save()
    }

    // Save element-level changes
    for (const element of this.elements.data) {
      await element.publishOptionsChange(this.atlas)
    }

    await this.runReport()
  }

  async discardChanges () {
    // Discard report-level changes
    if (!_.isEqual(this.reportOptions, this.initialOptions)) {
      this.reportFiltersModified = false
      this.reportOptions = { ...this.initialOptions }
    }

    // Discard element-level changes
    for (const element of this.elements.data) {
      await element.discardOptionsChange()
    }
    await this.runReport()
  }
}
